import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(
    () => localStorage.getItem("orbit_active_workspace") || null
  );
  const [tree, setTree] = useState({ folders: [], listsByFolder: {} });
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);

  const refreshWorkspaces = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await api.get("/workspaces");
      setWorkspaces(data.workspaces);
      let activeId = activeWorkspaceId;
      if (!activeId || !data.workspaces.find((w) => w.id === activeId)) {
        activeId = data.workspaces[0]?.id || null;
        setActiveWorkspaceId(activeId);
        if (activeId) localStorage.setItem("orbit_active_workspace", activeId);
        else localStorage.removeItem("orbit_active_workspace");
      }
    } finally {
      setLoading(false);
    }
  }, [user, activeWorkspaceId]);

  const refreshTree = useCallback(async () => {
    if (!activeWorkspaceId) { setTree({ folders: [], listsByFolder: {} }); return; }
    const { data } = await api.get(`/workspaces/${activeWorkspaceId}/folders`);
    const folders = data.folders || [];
    const listsByFolder = {};
    await Promise.all(folders.map(async (f) => {
      const { data: ld } = await api.get(`/folders/${f.id}/lists`);
      listsByFolder[f.id] = ld.lists || [];
    }));
    setTree({ folders, listsByFolder });
  }, [activeWorkspaceId]);

  const refreshUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data.users || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (user) { refreshWorkspaces(); refreshUsers(); } }, [user, refreshWorkspaces, refreshUsers]);
  useEffect(() => { if (activeWorkspaceId) refreshTree(); }, [activeWorkspaceId, refreshTree]);

  const switchWorkspace = (id) => {
    setActiveWorkspaceId(id);
    if (id) localStorage.setItem("orbit_active_workspace", id);
  };

  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId) || null;

  return (
    <WorkspaceContext.Provider value={{
      workspaces, activeWorkspace, activeWorkspaceId, switchWorkspace,
      tree, refreshTree, refreshWorkspaces, loading, users, refreshUsers,
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace must be used within WorkspaceProvider");
  return ctx;
}
